<template>
  <div class="barcode-scanner-wrapper p-3">
    <!-- USB ready indicator -->
    <div class="usb-indicator" :class="{ 'usb-flash': flashActive }">
      <span class="usb-dot"></span>
      ماسح USB جاهز
      <span v-if="lastScanned" class="ms-2 text-success fw-bold">{{ lastScanned }}</span>
    </div>

    <!-- Camera toggle -->
    <button
      class="btn btn-sm mt-2"
      :class="cameraScanning ? 'btn-danger' : 'btn-outline-secondary'"
      @click="toggleCamera"
    >
      <i class="pi" :class="cameraScanning ? 'pi-times' : 'pi-camera'"></i>
      {{ cameraScanning ? 'إغلاق الكاميرا' : 'تشغيل الكاميرا' }}
    </button>

    <div v-if="cameraScanning" class="camera-viewport mt-2">
      <div :id="scannerDivId"></div>
    </div>

    <p v-if="errorMsg" class="text-danger mt-1 mb-0 small">{{ errorMsg }}</p>
  </div>
</template>

<script>
import { Html5Qrcode } from 'html5-qrcode';

export default {
  name: 'BarcodeScanner',

  props: {
    mode: { type: String, required: true },
  },

  emits: ['scanned'],

  data() {
    return {
      cameraScanning: false,
      scanner: null,
      scannerDivId: `bc-reader-${Math.random().toString(36).substr(2, 9)}`,
      errorMsg: '',
      flashActive: false,
      lastScanned: '',

      keyBuffer:   [],
      lastKeyTime: 0,
      bufferTimer: null,
      // True once a burst has been recognized as "mid-scan" (2nd+ character
      // arriving within SCAN_BURST_GAP_MS of the previous one) — used to
      // preventDefault()/blur only once per burst, never on a lone keystroke
      // that might just be normal typing.
      burstActive: false,

      lastScan: { value: null, time: 0 },
      DEBOUNCE_MS: 2000,

      // A USB HID keyboard-wedge scanner sends its whole payload in a few
      // milliseconds per character — far faster than any human can type.
      // MIN_SCAN_LENGTH filters out stray 1-2 key noise; SCAN_BURST_GAP_MS is
      // the max gap allowed between two keystrokes for them to still count as
      // part of the same scanner burst (well below realistic human typing
      // speed, so normal typing in a focused field never accumulates a run
      // long/fast enough to be mistaken for a scan).
      MIN_SCAN_LENGTH: 3,
      SCAN_BURST_GAP_MS: 40,
    };
  },

  methods: {
    // ── USB keyboard scanner ────────────────────────────────────────────────

    // IMPORTANT: a keyboard-wedge scanner injects keystrokes into whichever
    // element already has focus — it does not change document.activeElement.
    // This used to `return` early whenever an <input>/<textarea>/<select> was
    // focused, which silently swallowed every scan whenever the page's search
    // box (the most commonly-focused element on both Attendance and Payments)
    // had focus. That was the actual bug behind "scanner reads the code but
    // nothing happens". Detection now relies purely on TIMING (see
    // SCAN_BURST_GAP_MS/MIN_SCAN_LENGTH above), not on what currently has
    // focus, so it works regardless of where the cursor happens to be.
    handleKeydown(e) {
      const now = Date.now();

      if (e.key === 'Enter') {
        if (!this.keyBuffer.length) return; // not ours — leave Enter alone elsewhere
        e.preventDefault();
        clearTimeout(this.bufferTimer);
        const buffered = this.keyBuffer.splice(0);
        this.burstActive = false;
        if (buffered.length >= this.MIN_SCAN_LENGTH) this.processScanned(buffered.join(''));
        return;
      }

      // Reset buffer if the gap since the last key is too large to be a
      // scanner burst (i.e. this looks like normal human typing).
      if (now - this.lastKeyTime > this.SCAN_BURST_GAP_MS && this.keyBuffer.length) {
        this.keyBuffer = [];
        this.burstActive = false;
        clearTimeout(this.bufferTimer);
      }

      if (e.key.length === 1) {
        if (this.keyBuffer.length) {
          // Already mid-burst (this is at least the 2nd character): it
          // belongs to the scanner, not to whatever element happens to be
          // focused. Prevent it from being typed into a visible field (e.g.
          // the "ابحث" search box) — a leaked keystroke there triggers a live
          // search re-render/table reflow on every character of the scan,
          // which is what made the page appear to auto-scroll.
          e.preventDefault();
          if (!this.burstActive) {
            this.burstActive = true;
            const active = document.activeElement;
            if (active && active !== document.body && typeof active.blur === 'function') {
              active.blur();
            }
          }
        }

        this.keyBuffer.push(e.key);
        this.lastKeyTime = now;

        clearTimeout(this.bufferTimer);
        this.bufferTimer = setTimeout(() => {
          const buffered = this.keyBuffer.splice(0);
          this.burstActive = false;
          if (buffered.length >= this.MIN_SCAN_LENGTH) this.processScanned(buffered.join(''));
        }, 200);
      }
    },

    // ── Parse & emit ────────────────────────────────────────────────────────

    // Printed barcodes (BarcodeLabel.vue) encode "STU-<id>"; printed QR codes
    // (students_qr.vue / students_qr_print.vue) encode the bare numeric id.
    // Anything else (reservation code, reservation id, ...) is passed through
    // as `raw` for the consumer to interpret.
    processScanned(raw) {
      console.log('[BarcodeScanner] QR/barcode detected');

      const value = String(raw ?? '').trim();
      if (!value) return;

      console.log('[BarcodeScanner] Decoded value:', value);

      const stuMatch = value.match(/^STU-(\d+)$/i);
      const studentId = stuMatch ? stuMatch[1] : (/^\d+$/.test(value) ? value : null);

      const dedupeKey = value;
      const now = Date.now();
      if (dedupeKey === this.lastScan.value && now - this.lastScan.time < this.DEBOUNCE_MS) {
        console.log('[BarcodeScanner] Duplicate scan ignored (within debounce window):', value);
        return;
      }

      this.lastScan      = { value: dedupeKey, time: now };
      this.lastScanned   = studentId ? `STU-${studentId}` : value;
      this.flashActive   = true;
      setTimeout(() => { this.flashActive = false; }, 600);

      this.$emit('scanned', { raw: value, studentId, mode: this.mode });
    },

    // ── Camera ──────────────────────────────────────────────────────────────

    async toggleCamera() {
      this.cameraScanning ? await this.stopCamera() : await this.startCamera();
    },

    async startCamera() {
      this.errorMsg      = '';
      this.cameraScanning = true;
      await this.$nextTick();

      try {
        this.scanner = new Html5Qrcode(this.scannerDivId);
        await this.scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 350, height: 100 } },
          (decoded) => this.processScanned(decoded),
          () => {}
        );
        console.log('[BarcodeScanner] Scanner initialized (camera)');
      } catch {
        this.errorMsg      = 'تعذّر الوصول إلى الكاميرا. يرجى السماح بالإذن وإعادة المحاولة.';
        this.cameraScanning = false;
      }
    },

    async stopCamera() {
      if (this.scanner?.isScanning) {
        try { await this.scanner.stop(); } catch {}
      }
      this.scanner       = null;
      this.cameraScanning = false;
    },
  },

  mounted() {
    document.addEventListener('keydown', this.handleKeydown);
    console.log('[BarcodeScanner] Scanner initialized (USB keyboard-wedge)', { mode: this.mode });
  },

  beforeUnmount() {
    document.removeEventListener('keydown', this.handleKeydown);
    clearTimeout(this.bufferTimer);
    this.stopCamera();
  },
};
</script>

<style scoped>
.barcode-scanner-wrapper {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
}

.usb-indicator {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #15803d;
  transition: background 0.3s;
}

.usb-indicator.usb-flash {
  background: #dcfce7;
  border-color: #22c55e;
}

.usb-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
  animation: pulse-dot 1.5s infinite;
}

@keyframes pulse-dot {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}

.camera-viewport {
  border: 2px solid #0d6efd;
  border-radius: 8px;
  overflow: hidden;
  max-width: 400px;
}
</style>
